/**
 * Renaming an entity from a text field: a local draft, committed as one
 * `entity.rename`, with the command's rejection (an empty name, a name that's
 * taken) kept as the field's error. Shared by the Inspector header and the
 * Outliner's inline rename, so both behave like Unreal's: commit on Enter or
 * blur, revert on Escape. A rename per keystroke would fill the undo stack and
 * could collide with another entity's name mid-word.
 */
import { useState } from "react";
import type { CommandOrigin } from "../../commands/types";
import type { EntityRecord } from "../../world/entity";
import { useEditor } from "../EditorContext";

export interface RenameDraft {
  readonly draft: string;
  readonly setDraft: (name: string) => void;
  /** Why the last commit was rejected, or null. */
  readonly error: string | null;
  /** Renames to the draft. True when done: renamed, or nothing to change. */
  readonly commit: () => boolean;
  /** Back to the entity's current name, clearing any error. */
  readonly revert: () => void;
}

export function useRename(entity: EntityRecord, origin: CommandOrigin): RenameDraft {
  const { dispatch } = useEditor();
  const [draft, setDraft] = useState(entity.name);
  const [error, setError] = useState<string | null>(null);

  const commit = () => {
    if (draft === entity.name) {
      setError(null);
      return true;
    }
    const result = dispatch({
      type: "entity.rename",
      payload: { entity: entity.id, name: draft },
      origin,
    });
    if (result instanceof Promise) return true;
    setError(result.ok ? null : result.error);
    return result.ok;
  };

  return {
    draft,
    setDraft,
    error,
    commit,
    revert: () => {
      setDraft(entity.name);
      setError(null);
    },
  };
}
